/**
 * AML Audit Trail Dashboard (F-005)
 * Comprehensive audit trail of identity verification checks for RRA 2025 compliance.
 */

import React, { useState, useEffect } from 'react';
import { amlService } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import CSVImportModal from '../Shared/CSVImportModal';
import { attachEvidenceFromZip } from '../../utils/bulkEvidenceUpload';

interface AmlCheck {
    id: string;
    tenantPersonName: string;
    method: string;
    outcome: string;
    checkedAt: string;
    verifier: {
        name: string;
        email: string;
    };
    notes?: string;
    documentRef?: string;
    integrity?: boolean;
}

const AMLDashboard: React.FC = () => {
    const [checks, setChecks] = useState<AmlCheck[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, passed: 0, pending: 0, failed: 0 });
    const [showBulkImport, setShowBulkImport] = useState(false);

    useEffect(() => {
        fetchChecks();
    }, []);

    const fetchChecks = async () => {
        try {
            setLoading(true);
            const res = await amlService.getChecks();
            const data = res.data.data || [];
            setChecks(data);

            // Calculate stats
            const s = {
                total: data.length,
                passed: data.filter((c: any) => c.outcome === 'PASSED' || c.outcome === 'SUCCESS').length,
                pending: data.filter((c: any) => c.outcome === 'PENDING').length,
                failed: data.filter((c: any) => c.outcome === 'FAILED' || c.outcome === 'REJECTED').length,
            };
            setStats(s);
        } catch (error) {
            console.error('Failed to load AML checks', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const res = await amlService.exportTrail();
            const data = res.data.data;
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `AML_Audit_Trail_${new Date().toISOString().split('T')[0]}.json`;
            a.click();
        } catch (error) {
            alert('Export failed');
        }
    };

    const getOutcomeBadge = (outcome: string) => {
        const colors: any = {
            'PASSED': 'bg-green-100 text-green-800',
            'SUCCESS': 'bg-green-100 text-green-800',
            'PENDING': 'bg-yellow-100 text-yellow-800',
            'FAILED': 'bg-red-100 text-red-800',
            'REJECTED': 'bg-red-100 text-red-800',
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${colors[outcome] || 'bg-gray-100 text-gray-800'}`}>
                {outcome}
            </span>
        );
    };

    const isStandalone = window.location.pathname === '/aml';

    return (
        <div className={`flex flex-col ${isStandalone ? 'min-h-screen bg-gray-50' : 'bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mt-4'}`}>
            {isStandalone && <Navbar />}
            <div className={`flex-1 w-full mx-auto ${isStandalone ? 'max-w-7xl px-4 sm:px-6 lg:px-8 py-8' : ''}`}>
                {!isStandalone && (
                    <div className="mb-6 pb-4 border-b border-gray-100">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center">
                            <i className="fas fa-fingerprint text-emerald-600 mr-2"></i>
                            AML Compliance Audit Trail
                        </h2>
                    </div>
                )}
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                            <i className="fas fa-fingerprint text-emerald-600 mr-3"></i>
                            AML Audit Trail
                        </h1>
                        <p className="mt-2 text-sm text-gray-600">
                            Immutable log of identity verification checks for Anti-Money Laundering compliance.
                        </p>
                    </div>
                    <div className="mt-4 flex gap-2 md:mt-0 md:ml-4">
                        <button
                            onClick={() => setShowBulkImport(true)}
                            title="Bulk upload existing AML check records from a CSV"
                            className="inline-flex items-center px-4 py-2 border border-emerald-200 rounded-lg shadow-sm text-sm font-medium text-emerald-700 bg-white hover:bg-emerald-50"
                        >
                            <i className="fas fa-file-csv mr-2"></i>
                            Bulk Upload
                        </button>
                        <button
                            onClick={handleExport}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <i className="fas fa-file-export mr-2"></i>
                            Export Audit Trail
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                        <p className="text-sm text-gray-500 mb-1">Total Checks</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                        <p className="text-sm text-gray-500 mb-1">Passed</p>
                        <p className="text-2xl font-bold text-green-600">{stats.passed}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                        <p className="text-sm text-gray-500 mb-1">Pending</p>
                        <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
                        <p className="text-sm text-gray-500 mb-1">Failed</p>
                        <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="p-20 text-center">
                            <div className="animate-spin h-10 w-10 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                            <p className="text-gray-500">Loading audit trail...</p>
                        </div>
                    ) : checks.length === 0 ? (
                        <div className="p-20 text-center text-gray-500 italic">
                            No AML checks recorded yet.
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Person Verified</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Method</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Outcome</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Verified By</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase">Integrity</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {checks.map((check) => (
                                    <tr key={check.id} className="hover:bg-emerald-50/30 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{check.tenantPersonName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{check.method.replace('_', ' ')}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">{getOutcomeBadge(check.outcome)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{check.verifier.name}</div>
                                            <div className="text-xs text-gray-500">{check.verifier.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(check.checkedAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <span className="text-emerald-600" title="Cryptographically Sealed - FRR-A 2025 compliant">
                                                <i className="fas fa-shield-alt"></i>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            {isStandalone && <Footer />}

            <CSVImportModal
                isOpen={showBulkImport}
                onClose={() => setShowBulkImport(false)}
                onImport={async (data, zipFile) => {
                    const VALID_OUTCOMES = ['PASS', 'FAIL', 'REFER', 'PENDING', 'EXPIRED'];
                    let rows: any[] = data.map((row: any, idx: number) => {
                        const outcome = String(row.outcome || '').toUpperCase().trim();
                        if (!VALID_OUTCOMES.includes(outcome)) {
                            throw new Error(`Row ${idx + 1}: outcome must be one of ${VALID_OUTCOMES.join(', ')}`);
                        }
                        return {
                            subjectName: row.subject_name,
                            propertyId: row.property_id || undefined,
                            method: row.method || 'MANUAL_VERIFICATION',
                            outcome,
                            checkedAt: row.check_date || undefined,
                            notes: row.notes || '',
                            evidence_filename: row.evidence_filename || '',
                        };
                    });
                    rows = await attachEvidenceFromZip(rows, zipFile, {
                        filenameField: 'evidence_filename',
                        resolvePropertyId: (row) => row.propertyId,
                    });
                    const checks = rows.map(({ evidence_filename, _attachStatus, ...rest }) => rest);
                    await amlService.bulkImportChecks(checks);
                    fetchChecks();
                }}
                title="Bulk Upload AML Checks"
                headerRequirements={[
                    { id: 'subject_name', label: 'Subject Name', required: true, description: 'The person or company checked', example: 'Jane Doe' },
                    { id: 'method', label: 'Method', required: false, description: 'e.g. PASSPORT, DRIVING_LICENCE, MANUAL_VERIFICATION', example: 'PASSPORT' },
                    { id: 'outcome', label: 'Outcome', required: true, description: 'PASS, FAIL, REFER, PENDING or EXPIRED', example: 'PASS' },
                    { id: 'check_date', label: 'Check Date', required: false, description: 'YYYY-MM-DD', example: '2026-01-15' },
                    { id: 'notes', label: 'Notes', required: false, description: 'Any extra details', example: '' },
                    { id: 'property_id', label: 'Property ID', required: false, description: 'Only needed if attaching a scanned ID document', example: 'PROP-0001' },
                    { id: 'evidence_filename', label: 'Evidence Filename', required: false, description: 'Filename inside the companion .zip to attach as the ID document checked (requires property_id)', example: 'jane_doe_passport.jpg' },
                ]}
                templateHeaders={['subject_name', 'method', 'outcome', 'check_date', 'notes', 'property_id', 'evidence_filename']}
                sampleData="Jane Doe,PASSPORT,PASS,2026-01-15,,PROP-0001,jane_doe_passport.jpg"
                allowFileAttachments
            />
        </div>
    );
};

export default AMLDashboard;
